import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyLandingPageHowItWorksItem from './LoyaltyLandingPageHowItWorksItem'
import Typography from 'LuxKit/Typography/Typography'

const HowItWorksSection = styled(Pane)`
  padding: ${rem(48)} 0;
`

const HowItWorksItems = styled.div`
  display: grid;
  gap: ${rem(44)};
  
  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

export interface LoyaltyLandingPageHowItWorksDetails {
  title: string
  description: string
  type: 'earn' | 'savings' | 'status'
}

function getHowItWorksItems(intl: ReturnType<typeof useIntl>): Array<LoyaltyLandingPageHowItWorksDetails> {
  return [
    {
      title: intl.formatMessage({
        id: 'zG3ImHS2LFkmL2A0ozJv',
        defaultMessage: 'Earn points on every booking',
        description: 'Loyalty how-it-works title for earning points step',
      }),
      description: intl.formatMessage({
        id: 'ziVgIYw4gcx5fTGlsMET',
        defaultMessage: 'Look for the rewards badge on offers to see how many points you’ll earn. Keep an eye out for bonus offers too.',
        description: 'Loyalty how-it-works description explaining how to earn points on bookings',
      }),
      type: 'earn',
    },
    {
      title: intl.formatMessage({
        id: 'yVVW2FSQ6GYyFg3LqPux',
        defaultMessage: 'Turn points into savings',
        description: 'Loyalty how-it-works title for using points step in the flow',
      }),
      description: intl.formatMessage({
        id: 'duJZR2d1ouw3XbBohxtJ',
        defaultMessage: 'Use your points to pay for all or part of your next escape — simply select the amount at checkout and save instantly.',
        description: 'Loyalty how-it-works description explaining how to redeem points at checkout',
      }),
      type: 'savings',
    },
    {
      title: intl.formatMessage({
        id: '81qie_GAqRJEi2lHOZxu',
        defaultMessage: 'Unlock tiers with Status Credits and Nights',
        description: 'Loyalty how-it-works section title for the tier progression step mentioning Status Credits and Nights',
      }),
      description: intl.formatMessage({
        id: 'gyo2ESTU30vKz6qspzGa',
        defaultMessage: 'Earn Status Credits and Nights every time you travel to move closer to the next tier and more VIP perks.',
        description: 'Loyalty how-it-works section body explaining earning Status Credits and Nights toward the next tier',
      }),
      type: 'status',
    },
  ]
}

interface Props {
  onJoin?: () => void
  ctaText?: string
}

function LoyaltyLandingPageHowItWorksSection(props: Props) {
  const intl = useIntl()
  const { onJoin, ctaText } = props

  const howItWorksItems = getHowItWorksItems(intl)

  const headingText = intl.formatMessage({
    id: 'MviJBIhVC8SipSIfMqig',
    defaultMessage: 'How it works',
    description: 'Heading for the loyalty program how-it-works section',
  })
  const subtitleText = intl.formatMessage({
    id: 'I7KU5tFzq6_zsjp9bPmO',
    defaultMessage: 'Earn points and Status Credits every time you travel — and unlock a world of rewards.',
    description: 'Subtitle explaining the loyalty program rewards overview',
  })
  const joinButtonText = intl.formatMessage({
    id: 'Pd4xq-vBV7bSfTUpKONR',
    defaultMessage: 'Join now - it’s free',
    description: 'Call-to-action button to join the free loyalty program',
  })

  return <HowItWorksSection background="neutral-seven">
    <LayoutContainer variant="medium">
      <VerticalSpacer gap={48}>
        <VerticalSpacer gap={8}>
          <Heading variant="heading2" align="center">{headingText}</Heading>
          <BodyText variant="large" align="center">
            {subtitleText}
          </BodyText>
        </VerticalSpacer>
        <HowItWorksItems>
          {howItWorksItems.map(item => <LoyaltyLandingPageHowItWorksItem
            key={item.title}
            item={item}
          />)}
        </HowItWorksItems>
        {onJoin && <Typography align="center">
          <TextButton kind="primary" onClick={onJoin}>
            {ctaText ?? joinButtonText}
          </TextButton>
        </Typography>}
      </VerticalSpacer>
    </LayoutContainer>
  </HowItWorksSection>
}

export default LoyaltyLandingPageHowItWorksSection
